<template>
  <div class="container">
    <md-radio value="immediate" v-model="_computedMode">
      即時実行
    </md-radio>
    <md-radio value="reserve" v-model="_computedMode">
      スケジュール登録
    </md-radio>
    <div v-if="_computedMode === 'reserve'">
      <md-card>
        <md-card-header :style="{ padding: '8px' }">
          <div class="md-title reserve-count-title">
            予約数
            <span
              v-if="_errors.reservationLimit"
              class="reservation-limit-warning"
              :gl-tooltip="'予約管理で整理する'"
              gl-tooltip-direction="right"
            >
              ※上限に達しています！
              <gl-button
                class="reservation-limit-button"
                @click="transitionQueryManagement()"
                icon-button
              >
                <md-icon>manage_search</md-icon>
              </gl-button>
            </span>
          </div>
          <!-- <md-badge :md-content="`${_current}/${_limit}`" class="md-primary" /> -->
        </md-card-header>

        <md-card-content>
          <md-progress-bar
            md-mode="determinate"
            :md-value="Math.min(100, Math.round((_current / _limit) * 100))"
          />
          <div style="margin-top: 8px;">
            {{ _current }} / {{ _limit }} 件（ 残り
            {{ Math.max(0, _limit - _current) }} 件）
          </div>
          <div v-if="wasRequiresUpdate" class="requires-update-warning">
            <md-icon class="warning-icon">warning</md-icon>
            <span
              >この予約は更新が必要です。内容を確認・更新してください。</span
            >
          </div>
        </md-card-content>
      </md-card>
      <div class="flex-col padding-top">
        <div class="title">
          <gl-sub-text>
            予約実行日
            <span v-if="_originalScheduledDate" style="margin-left: 8px;">
              (前回: {{ _originalScheduledDate }})
            </span>
          </gl-sub-text>
        </div>
        <div class="flex-row">
          <gl-input-date
            :min="minScheduledDate"
            :max="maxScheduledDate"
            :value.sync="_computedScheduledDate"
          />
          <span v-if="_errors.scheduledDate" class="error">
            {{ '※' + _errors.scheduledDate }}
          </span>
        </div>
      </div>
      <div class="flex-col">
        <div class="title">
          <gl-sub-text>予約タイトル</gl-sub-text>
        </div>
        <md-field>
          <md-input v-model="_computedTitle" placeholder="タイトルを入力" />
        </md-field>
      </div>
      <div class="flex-col">
        <div class="title">
          <gl-sub-text>
            分析実行周期
            <span @click="showCycleTypeDetailDialog = true">
              <md-icon class="cycle-type-info-icon">
                info
              </md-icon>
            </span>
            <span class="cycle-type-description">
              毎月周期は月の日数による日付のずれが生じる可能性があります
            </span>
            <span v-if="_errors.cycleType" class="error">
              {{ '※' + _errors.cycleType }}
            </span>
          </gl-sub-text>
        </div>
        <md-field>
          <md-select v-model="_computedCycleType">
            <md-option
              v-for="option in cycleTypeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </md-option>
          </md-select>
        </md-field>
      </div>
    </div>

    <md-dialog :md-active.sync="showCycleTypeDetailDialog">
      <md-dialog-title
        >📅 毎月周期における日付の自動補正について</md-dialog-title
      >

      <md-dialog-content>
        <p>
          「毎月」を指定した場合、実行月の日数（28日〜31日）に合わせて日付が自動調整されます。
        </p>

        <p style="font-weight: bold; color: #d32f2f;">
          【注意：日付の固定化について】
        </p>
        <p>
          開始日や終了日、予約実行日に「29日〜31日」が含まれている場合、2月や小月（30日までの月）を経過すると、<strong>それ以降の月も繰り上げられた日付（28日や30日など）で固定</strong>されます。
        </p>

        <p style="font-weight: bold;">
          例（31日指定の場合）：
        </p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>1月31日 ➔ 2月28日（2月末日に自動補正）</li>
          <li>2月28日 ➔ <strong>3月28日</strong>（※31日には戻りません）</li>
        </ul>

        <p style="margin-top: 12px;">
          毎月正確な「1日〜末日」のデータを対象としたい場合は、期間選択で<strong>「月」</strong>をご利用ください。
        </p>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button
          class="md-primary"
          @click="showCycleTypeDetailDialog = false"
        >
          閉じる
        </md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { SET_RESERVE_MAX_DATE } from '@/const/mutation-types'
import { STATUSES } from '@/const/statusConfig'

export default {
  components: {},
  data() {
    return {
      VALID: 'VALID',
      EXPIRED: 'EXPIRED',
      DISABLE: 'DISABLE',
      reservationLimit: 0,
      reservationActiveCount: 0,
      showCycleTypeDetailDialog: false,
    }
  },
  props: {
    /**
     * 予約モードかどうか（即時実行 or 予約実行）
     * 'immediate'（即時実行）, 'reserve'（予約実行）
     */
    mode: {
      type: String,
    },
    /**
     * 更新用データか、新規登録用データなのか
     */
    isEdit: {
      type: Boolean,
      default: false,
    },
    /**
     * 予約ｊ（予約実行の時）
     * 形式はYYYYMMDD（例: 20231231）
     */
    scheduledDate: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    title: {
      type: String,
    },
    status: {
      type: String,
      validator(value) {
        return STATUSES.includes(value)
      },
    },
    /**
     * 予約の実行間隔（定期実行の時）
     * 'ONCE'（今回のみ）, 'WEEKLY'（毎週）, 'MONTHLY'（毎月）
     */
    cycleType: {
      type: String,
    },
    /**
     * 利用可能な実行周期のリスト
     * 親コンポーネントから termType に基づいて計算されたリストが渡される
     * デフォルト値は全選択肢
     */
    availableCycleTypes: {
      type: Array,
      default: () => ['ONCE', 'WEEKLY', 'MONTHLY', 'MONTHLY_EXACT'],
      validator(value) {
        const validTypes = ['ONCE', 'WEEKLY', 'MONTHLY', 'MONTHLY_EXACT']
        return Array.isArray(value) && value.every(v => validTypes.includes(v))
      },
    },
    /**
     * 復元時の元の予約設定（変更点洗い出し用）
     */
    originalConfig: {
      type: Object,
      default: undefined,
    },
  },
  watch: {
    mode() {
      this.syncReserveMaxDate()
    },
    scheduledDate() {
      this.syncReserveMaxDate()
    },
    '$route.query.reservationUpdatedAt'() {
      this.resetReservationQuota()
    },
    availableCycleTypes(newValue) {
      // 利用可能な周期が変更された場合、現在の値が有効かチェック
      if (
        this._computedCycleType &&
        !newValue.includes(this._computedCycleType)
      ) {
        // 現在の値が利用不可の場合、リストの最初の値にリセット
        this.$emit('update:cycleType', newValue[0])
      }
    },
    _errors: {
      immediate: true,
      deep: true,
      handler(newErrors) {
        const errorArray = Object.values(newErrors).filter(Boolean)
        this.$emit('update:errors', errorArray)
      },
    },
  },
  computed: {
    minScheduledDate() {
      const currentDate = process.env.VUE_APP_SCHEDULE_MIN_DATE
        ? moment(process.env.VUE_APP_SCHEDULE_MIN_DATE)
        : moment()
      return currentDate.format('YYYYMMDD')
    },
    maxScheduledDate() {
      return moment()
        .add(1, 'year')
        .format('YYYYMMDD')
    },
    // 編集モードかどうか
    _isEdit: {
      get() {
        return this.isEdit
      },
    },
    // 現在の予約数
    _current: {
      get() {
        return this.reservationActiveCount
      },
    },
    // 予約数の上限
    _limit: {
      get() {
        return this.reservationLimit
      },
    },
    // 予約数の上限に達しているかどうか
    _isReservationLimitReached() {
      // 予約数の上限が未取得(初期値の0)の場合、上限到達扱いしない
      if (this._limit === 0) return false
      return this._current >= this._limit
    },
    _computedMode: {
      get() {
        return this.mode
      },
      set(value) {
        this.$emit('update:mode', value)
      },
    },
    _computedScheduledDate: {
      get() {
        return this.scheduledDate
      },
      set(value) {
        this.$emit('update:scheduledDate', value)
      },
    },
    // 元の予約実行日（前回の予約実行日）を表示用にフォーマットして返す
    _originalScheduledDate() {
      if (
        !this.originalConfig ||
        !this.originalConfig.scheduledDate ||
        this.originalConfig.status !== 'NextReservationFailed'
      ) {
        return null
      }
      const date = moment(this.originalConfig.scheduledDate, 'YYYYMMDD', true)
      return date.isValid() ? date.format('YYYY年MM月DD日') : null
    },
    _computedTitle: {
      get() {
        return this.title
      },
      set(value) {
        this.$emit('update:title', value === '' ? undefined : value)
      },
    },
    // TODO STEP3で定期実行するときに利用する
    _computedCycleType: {
      get() {
        return this.cycleType
      },
      set(value) {
        this.$emit('update:cycleType', value)
      },
    },
    /**
     * 利用可能な実行周期の選択肢
     * availableCycleTypesに基づいて、表示用のオプションリストを生成
     * ONCE: 今回のみ
     * WEEKLY: 毎週
     * MONTHLY: 毎月（補正有りで、定期実行の次回期間算出時、月初/月末の日付の補正する。）
     * MONTHLY_EXACT: 毎月（補正なしなので、単純に月を1加算する。）
     */
    cycleTypeOptions() {
      const allOptions = [
        { value: 'ONCE', label: '今回のみ' },
        { value: 'WEEKLY', label: '毎週' },
        { value: 'MONTHLY', label: '毎月' },
        { value: 'MONTHLY_EXACT', label: '毎月' },
      ]
      return allOptions.filter(option =>
        this.availableCycleTypes.includes(option.value),
      )
    },
    /**
     * 元の予約ステータスが RequiresUpdate だったかどうか
     */
    wasRequiresUpdate() {
      return (
        this.originalConfig && this.originalConfig.status === 'RequiresUpdate'
      )
    },
    _errors() {
      const errors = {}
      if (this._isReservationLimitReached) {
        // 予約数が上限かつ、新規登録orエラー状態の予約の更新の場合はエラーとする
        if (!this._isEdit || (this.status === 'Error' && this._isEdit)) {
          errors.reservationLimit =
            '予約数が上限に達しているため、新規の予約はできません。'
        }
      }
      if (!this._computedScheduledDate) {
        errors.scheduledDate = '予約実行日を指定してください。'
        return errors
      }
      const scheduledDate = moment(
        this._computedScheduledDate,
        'YYYYMMDD',
        true,
      )
      if (!scheduledDate.isValid()) {
        errors.scheduledDate = '予約実行日の形式が不正です。'
        return errors
      }

      const minDate = moment(this.minScheduledDate, 'YYYYMMDD', true)
      const maxDate = moment(this.maxScheduledDate, 'YYYYMMDD', true)
      if (minDate.isValid() && maxDate.isValid()) {
        if (
          scheduledDate.isBefore(minDate, 'day') ||
          scheduledDate.isAfter(maxDate, 'day')
        ) {
          errors.scheduledDate = `予約日時は ${minDate.format(
            'YYYY/MM/DD',
          )} 〜 ${maxDate.format('YYYY/MM/DD')} の範囲で指定してください。`
        }
      }
      return errors
    },
  },
  mounted() {
    this.resetReservationQuota()
  },
  beforeDestroy() {
    this.$store.commit(SET_RESERVE_MAX_DATE, undefined)
  },
  methods: {
    syncReserveMaxDate() {
      if (this.mode !== 'reserve') {
        this.$store.commit(SET_RESERVE_MAX_DATE, undefined)
        return
      }

      const date = moment(this.scheduledDate, 'YYYYMMDD', true)
      if (!date.isValid()) {
        this.$store.commit(SET_RESERVE_MAX_DATE, undefined)
        return
      }

      this.$store.commit(
        SET_RESERVE_MAX_DATE,
        date.subtract(1, 'days').format('YYYYMMDD'),
      )
    },
    async resetReservationQuota() {
      const reservationQuota = await this.$repositories
        .get('queryReservation')
        .getReservationQuota()
      this.reservationLimit = reservationQuota.reservationLimit
      this.reservationActiveCount = reservationQuota.reservationActiveCount
    },
    transitionQueryManagement() {
      this.$router.replace({
        path: '/query-management',
        query: { reservationMode: true },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$header-height: 72px;
$filter-summary-width: 320px;

// サマリの各条件ホバー時の背景色変更を無くす
.filter-summary-wrapper /deep/ .wrapper:hover {
  background: none !important;
}

// 各条件のコンテナ
/deep/ .container {
  color: black !important;
  padding: 2px !important;

  display: flex;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid var(--color-border);
}
// アイコンエリア
/deep/ .icon-area {
  flex: 0 0 auto;
}
// アイコン本体
/deep/ .gl-icon {
  color: black !important;
}
// コンテンツ（右側の条件）
/deep/ .content {
  flex: 1;
  min-width: 0;
}
// 各条件のテキスト
/deep/ .summary {
  color: black !important;

  width: 100%;
  overflow-wrap: break-word;
  word-break: break-all;
}

/deep/ .summary > div {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
}

/deep/ .summary > div > div:nth-child(odd) {
  width: 10%;
}

// termCompare、termPosの比較の場合だけに適用される
/deep/ .summary > div > div:nth-child(even) {
  width: 90%;
  // flex: ;
  text-align: left !important;
  padding: 2px 0;
}

// サマリ下部のボタン群（「分析結果を確認する」しかないけど）
.summary-button {
  display: flex;
  justify-content: flex-end;
}

// 「分析結果を確認する」ボタンのツールチップ
.tooltip-wrapper {
  display: inline-block;
}

.reserve-count-title {
  font-size: 16px;
  line-height: 1.4;
}

.reservation-limit-warning {
  display: inline-flex;
  align-items: center;
  margin-left: var(--spacing-medium);
  color: var(--color-error, #e53935);
  font-size: 14px;
}

.reservation-limit-button {
  margin-left: 2px;
}

.flex-row {
  display: flex;
  flex-direction: row;
}
.flex-col {
  display: flex;
  flex-direction: column;
}
.padding-right {
  padding-right: var(--spacing-small);
}
.padding-left {
  padding-right: var(--spacing-small);
}
.padding-top {
  padding-top: var(--spacing-small);
}
.title {
  margin-top: var(--spacing-medium);
}
.error {
  color: var(--color-error, #e53935);
  margin-left: var(--spacing-small);
}

.requires-update-warning {
  display: flex;
  align-items: center;
  margin-top: var(--spacing-medium);
  padding: var(--spacing-small);
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 13px;
}

.warning-icon {
  margin-right: var(--spacing-small);
  color: #ffc107;
  font-size: 18px;
  flex-shrink: 0;
}

.cycle-type-description {
  display: inline;
  color: #bbb;
  font-size: 11px;
  margin-left: 6px;
  line-height: 1.4;
}

.cycle-type-info-icon {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.2s ease;
  margin-left: var(--spacing-small);
  color: #1976d2;

  &:hover {
    color: #42a5f5;
  }
}
</style>
